<template>
  <div class="text-center d-inline-block">
    <v-dialog v-model="dialog" max-width="560" persistent content-class="account-settings-dialog">
      <template #activator="{ on, attrs }">
        <v-tooltip bottom>
          <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <button
              class="action-pill pill-dark"
              v-bind="{ ...attrs, ...tooltipAttrs }"
              v-on="{ ...on, ...tooltipOn }"
            >
              <v-icon small>mdi-account-cog-outline</v-icon>
              <span>Mi cuenta</span>
            </button>
          </template>
          <span>Configuración de tu cuenta</span>
        </v-tooltip>
      </template>

      <v-card class="user-modal" elevation="0">

        <!-- Header -->
        <div class="modal-header">
          <div class="header-left">
            <div class="header-icon-wrap">
              <v-icon color="white" size="20">mdi-account-cog-outline</v-icon>
            </div>
            <div>
              <div class="modal-title">Configuración de cuenta</div>
              <div class="modal-subtitle">{{ currentUser ? currentUser.name : 'Gestiona tu cuenta' }}</div>
            </div>
          </div>
          <v-btn icon small @click="close" class="close-btn">
            <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div class="scroll-area">

            <!-- Editar perfil -->
            <div class="section-label">
              <v-icon size="13" color="#2D4B95" class="mr-1">mdi-account-outline</v-icon>
              Perfil
            </div>
            <div class="settings-card">
              <div class="settings-card-icon">
                <v-icon size="18" color="#2D4B95">mdi-account-edit-outline</v-icon>
              </div>
              <div class="settings-card-content">
                <div class="settings-card-title">Editar mi perfil</div>
                <div class="settings-card-desc">Actualiza tu nombre, teléfono, dirección o imagen de perfil.</div>
              </div>
              <button type="button" class="btn-action" @click="handleEditProfile">
                Editar
              </button>
            </div>

            <!-- Desautorizar políticas -->
            <div class="section-label mt-5">
              <v-icon size="13" color="#9A6F0F" class="mr-1">mdi-shield-alert-outline</v-icon>
              Privacidad
            </div>
            <div class="settings-card warning-pill">
              <div class="settings-card-icon">
                <v-icon size="18" color="#9A6F0F">mdi-shield-off-outline</v-icon>
              </div>
              <div class="settings-card-content">
                <div class="settings-card-title">Desautorizar políticas de privacidad</div>
                <div class="settings-card-desc">
                  Al revocar tu aceptación, <b>no podrás seguir usando el panel</b> hasta que aceptes nuevamente las políticas de privacidad.
                </div>
              </div>
              <button type="button" class="btn-action warning-pill" :disabled="loadingRevoke" @click="openConfirm('revoke')">
                <v-progress-circular v-if="loadingRevoke" indeterminate size="14" width="2" color="#9A6F0F"></v-progress-circular>
                <span v-else>Desautorizar</span>
              </button>
            </div>

            <!-- Exportar datos -->
            <div class="section-label mt-5">
              <v-icon size="13" color="#3EBFA3" class="mr-1">mdi-database-export-outline</v-icon>
              Mis datos
            </div>
            <div class="settings-card export-pill">
              <div class="settings-card-icon">
                <v-icon size="18" color="#3EBFA3">mdi-export-variant</v-icon>
              </div>
              <div class="settings-card-content">
                <div class="settings-card-title">Exportar mis datos</div>
                <div class="settings-card-desc">
                  Consulta o descarga una copia de tu información personal (derecho de portabilidad, Art. 14 Ley 21.719).
                </div>
              </div>
              <button type="button" class="btn-action export-pill" @click="showExportModal = true">
                Ver mis datos
              </button>
            </div>

            <!-- Eliminar / cancelar eliminación de cuenta -->
            <div class="section-label mt-5">
              <v-icon size="13" :color="hasPendingDeletion ? '#9A6F0F' : '#F4516C'" class="mr-1">mdi-alert-octagon-outline</v-icon>
              Zona de riesgo
            </div>
            <!-- Cancelar eliminación (solicitud pendiente) -->
            <div v-if="hasPendingDeletion" class="settings-card warning-pill">
              <div class="settings-card-icon">
                <v-icon size="18" color="#9A6F0F">mdi-account-clock-outline</v-icon>
              </div>
              <div class="settings-card-content">
                <div class="settings-card-title">Eliminación de cuenta pendiente</div>
                <div class="settings-card-desc" v-if="deletionConfirmed">
                  Tu cuenta será eliminada en <b>{{ daysLeftToCancel }} día{{ daysLeftToCancel === 1 ? '' : 's' }}</b>.
                  Cancela antes de que se cumpla el plazo si cambiaste de opinión.
                </div>
                <div class="settings-card-desc" v-else>
                  Solicitud creada. <b>Revisa tu correo y confírmala</b> para activar el período de gracia de 14 días.
                </div>
              </div>
              <button type="button" class="btn-action warning-pill" :disabled="loadingDelete" @click="openConfirm('cancel-delete')">
                <v-progress-circular v-if="loadingDelete" indeterminate size="14" width="2" color="#9A6F0F"></v-progress-circular>
                <span v-else>Cancelar eliminación</span>
              </button>
            </div>
            <!-- Solicitar eliminación (sin solicitud previa) -->
            <div v-else class="settings-card danger">
              <div class="settings-card-icon">
                <v-icon size="18" color="#F4516C">mdi-account-remove-outline</v-icon>
              </div>
              <div class="settings-card-content">
                <div class="settings-card-title">Eliminar cuenta</div>
                <div class="settings-card-desc">
                  Solicita la eliminación definitiva de tu cuenta. <b>Esta acción no se puede deshacer.</b>
                </div>
              </div>
              <button type="button" class="btn-action danger" :disabled="loadingDelete" @click="openConfirm('delete')">
                <v-progress-circular v-if="loadingDelete" indeterminate size="14" width="2" color="#F4516C"></v-progress-circular>
                <span v-else>Solicitar eliminación</span>
              </button>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="btn-cancel" @click="close">Cerrar</button>
        </div>

      </v-card>
    </v-dialog>

    <AcceptCancelModal
      :acceptRejectModal="confirmModal"
      :acceptRejectModalData="confirmModalData"
      :closeModal="closeModalConfirm"
    />
    <UserDataExportModal v-model="showExportModal" />
  </div>
</template>

<script>
import { deleteAccountRequest, cancelAccountDeletion, rejectAllComplianceConsents } from '@/helpers/api/user'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal.vue'
import UserDataExportModal from '@/components/Globals/Modals/Users/UserDataExportModal.vue'

export default {
  name: 'AccountSettingsModal',
  components: { AcceptCancelModal, UserDataExportModal },
  props: {
    user: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    dialog: false,
    loadingRevoke: false,
    loadingDelete: false,
    confirmModal: false,
    confirmModalData: null,
    pendingAction: null,
    showExportModal: false,
  }),
  computed: {
    currentUser(){
      return this.user || this.$store.userStore.state.userInfo
    },
    hasPendingDeletion(){
      const dr = this.currentUser?.delete_request
      return !!(dr && !dr.anonymized_at)
    },
    deletionConfirmed(){
      return !!(this.currentUser?.delete_request?.confirmed_at)
    },
    daysLeftToCancel(){
      const confirmedAt = this.currentUser?.delete_request?.confirmed_at
      if (!confirmedAt) return 14
      const deadline = new Date(new Date(confirmedAt).getTime() + 14 * 24 * 60 * 60 * 1000)
      const diff = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24))
      return Math.max(0, diff)
    }
  },
	mounted(){
		if(this.$route.query.action === 'account_settings'){
			this.dialog = true
		}
	},
  methods: {
    close(){
      this.dialog = false
    },
    handleEditProfile(){
      this.editUser(this.currentUser)
      this.close()
    },
    editUser (user) {
      this.$store.usersStore.commit('setSelectedUserInfo', user)
      this.$store.usersStore.commit('setEditUserModal', true)
    },
    openConfirm(action){
      this.pendingAction = action
      if(action === 'revoke'){
        this.confirmModalData = {
          title: 'Desautorizar políticas de privacidad',
          content_a: 'Si continúas, se revocará tu aceptación de las políticas de privacidad y no podrás seguir utilizando el panel hasta aceptarlas nuevamente. ¿Deseas continuar?',
          type: 'warning-pill',
          acceptLabel: 'Desautorizar'
        }
      }
      else if(action === 'delete'){
        this.confirmModalData = {
          title: 'Eliminar cuenta',
          content_a: 'Esta acción solicitará la eliminación definitiva de tu cuenta y no se puede deshacer. ¿Deseas continuar?',
          type: 'danger',
          acceptLabel: 'Eliminar cuenta'
        }
      }
      else if(action === 'cancel-delete'){
        this.confirmModalData = {
          title: 'Cancelar eliminación de cuenta',
          content_a: '¿Deseas cancelar la solicitud de eliminación? Tu cuenta quedará activa nuevamente.',
          type: 'warning-pill',
          acceptLabel: 'Sí, cancelar eliminación'
        }
      }
      this.confirmModal = true
    },
    async closeModalConfirm(status = false){
      const action = this.pendingAction
      this.confirmModal = false
      this.confirmModalData = null
      this.pendingAction = null
      if(status){
        if(action === 'revoke'){
          await this.handleRejectConsents()
        }
        else if(action === 'delete'){
          await this.handleDeleteAccount()
        }        else if(action === 'cancel-delete'){
          await this.handleCancelDelete()
        }      }
    },
    async handleRejectConsents(){
      this.loadingRevoke = true
      try{
        const res = await rejectAllComplianceConsents()
        if(res && res.code === 200){
					this.$store.userStore.dispatch('getUserInfo')
          this.setMessage({
            message: 'Se ha revocado tu aceptación de las políticas de privacidad',
            title: 'Consentimiento revocado',
            type: 'success',
            icon: 'mdi-check-circle',
            color: '#3EBFA3',
          })
          this.close()
          this.$emit('consent-rejected')
        }
        else {
          this.setMessage({
            message: 'No se pudo procesar la solicitud, intenta nuevamente',
            title: 'Error',
            type: 'danger',
            icon: 'mdi-close-octagon',
            color: '#F4516C',
          })
        }
      }
      catch(e){
        this.setMessage({
          message: 'No se pudo procesar la solicitud, intenta nuevamente',
          title: 'Error',
          type: 'danger',
          icon: 'mdi-close-octagon',
          color: '#F4516C',
        })
      }
      this.loadingRevoke = false
    },
    async handleCancelDelete(){
      this.loadingDelete = true
      try{
        const res = await cancelAccountDeletion()
        if(res && res.code === 200){
          this.$store.userStore.dispatch('getUserInfo')
          this.setMessage({
            message: 'La solicitud de eliminación fue cancelada. Tu cuenta permanece activa.',
            title: 'Eliminación cancelada',
            type: 'success',
            icon: 'mdi-check-circle',
            color: '#3EBFA3',
          })
          this.close()
        }
        else {
          this.setMessage({
            message: 'No se pudo cancelar la solicitud, intenta nuevamente',
            title: 'Error',
            type: 'danger',
            icon: 'mdi-close-octagon',
            color: '#F4516C',
          })
        }
      }
      catch(e){
        this.setMessage({
          message: 'No se pudo cancelar la solicitud, intenta nuevamente',
          title: 'Error',
          type: 'danger',
          icon: 'mdi-close-octagon',
          color: '#F4516C',
        })
      }
      this.loadingDelete = false
    },
    async handleDeleteAccount(){
      this.loadingDelete = true
      try{
        const res = await deleteAccountRequest()
        if(res && res.code === 200){
          this.setMessage({
            message: 'Tu solicitud de eliminación de cuenta fue enviada correctamente',
            title: 'Solicitud enviada',
            type: 'success',
            icon: 'mdi-check-circle',
            color: '#3EBFA3',
          })
          this.close()
          this.$emit('account-deletion-requested')
        }
        else {
          this.setMessage({
            message: 'No se pudo procesar la solicitud, intenta nuevamente',
            title: 'Error',
            type: 'danger',
            icon: 'mdi-close-octagon',
            color: '#F4516C',
          })
        }
      }
      catch(e){
        this.setMessage({
          message: 'No se pudo procesar la solicitud, intenta nuevamente',
          title: 'Error',
          type: 'danger',
          icon: 'mdi-close-octagon',
          color: '#F4516C',
        })
      }
      this.loadingDelete = false
    },
    setMessage(messageData){
      const data = {
        message: messageData.message,
        title: messageData.title,
        created: new Date(),
        type: messageData.type,
        icon: messageData.icon,
        color: messageData.color,
      }
      this.$store.globalStore.dispatch('addMessage', data)
    },
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }

.user-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  display: flex !important;
  flex-direction: column !important;
  max-height: 88vh;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 16px;
  background: linear-gradient(135deg, #2D4B95 0%, #1a2f63 100%);
  flex-shrink: 0;
}
.header-left { display: flex; align-items: center; gap: 12px; }
.header-icon-wrap {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.modal-title { font-size: 14px; font-weight: 600; color: #fff; line-height: 1.2; }
.modal-subtitle { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 2px; }
.close-btn { background: rgba(255,255,255,0.1) !important; border-radius: 8px !important; }

.modal-body {
  padding: 0;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.scroll-area {
  overflow-y: auto;
  padding: 20px 22px;
  flex: 1 1 auto;
  min-height: 0;
}

.section-label {
  font-size: 11px; font-weight: 600; color: #2D4B95;
  text-transform: uppercase; letter-spacing: 0.07em;
  display: flex; align-items: center; margin-bottom: 10px;
}

/* Settings cards */
.settings-card {
  display: flex; align-items: flex-start; gap: 13px;
  padding: 14px 15px; border-radius: 12px;
  border: 1.5px solid #e2e8f4; background: #f7f9fc;
}
.settings-card-icon {
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  background: #eef2fb;
  display: flex; align-items: center; justify-content: center;
}
.settings-card-content { flex: 1 1 auto; min-width: 0; }
.settings-card-title { font-size: 13px; font-weight: 600; color: #2d3748; line-height: 1.3; }
.settings-card-desc { font-size: 11.5px; color: #8896b3; margin-top: 3px; line-height: 1.5; }

.btn-action {
  flex-shrink: 0; align-self: center;
  padding: 8px 14px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #2D4B95, #1a2f63);
  font-size: 12px; font-weight: 600; color: white;
  cursor: pointer; white-space: nowrap;
  display: flex; align-items: center; justify-content: center;
  min-width: 84px; min-height: 32px;
  box-shadow: 0 2px 8px rgba(45,75,149,0.25);
}
.btn-action:hover:not(:disabled) { filter: brightness(1.08); }
.btn-action:disabled { opacity: 0.7; cursor: not-allowed; }

/* warning-pill variant */
.settings-card.warning-pill {
  border-color: rgba(154,111,15,0.22); background: #fbf5e8;
}
.settings-card.warning-pill .settings-card-icon { background: rgba(154,111,15,0.12); }
.settings-card.warning-pill .settings-card-icon .v-icon { color: #9A6F0F !important; }
.btn-action.warning-pill {
  background: #ffffff; border: 1.5px solid #9A6F0F; color: #8a6210;
  box-shadow: none;
}
.btn-action.warning-pill:hover:not(:disabled) { background: #9A6F0F; color: #ffffff; }

/* Danger variant */
.settings-card.danger {
  border-color: rgba(244,81,108,0.3); background: #fdf1f3;
}
.settings-card.danger .settings-card-icon { background: rgba(244,81,108,0.12); }
.btn-action.danger {
  background: #ffffff; border: 1.5px solid #F4516C; color: #F4516C;
  box-shadow: none;
}
.btn-action.danger:hover:not(:disabled) { background: #F4516C; color: #ffffff; }

/* Export variant */
.settings-card.export-pill {
  border-color: rgba(62,191,163,0.25); background: #f0faf7;
}
.settings-card.export-pill .settings-card-icon { background: rgba(62,191,163,0.12); }
.btn-action.export-pill {
  background: #ffffff; border: 1.5px solid #3EBFA3; color: #2a9e84;
  box-shadow: none;
}
.btn-action.export-pill:hover:not(:disabled) { background: #3EBFA3; color: #ffffff; }

/* Footer */
.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px; padding: 13px 22px;
  border-top: 1px solid #e8edf5; background: #f7f9fc;
  flex-shrink: 0;
}
.btn-cancel {
  padding: 8px 16px; border-radius: 8px;
  border: 1.5px solid #d1d9e8; background: transparent;
  font-size: 13px; font-weight: 500; color: #5a6a8a; cursor: pointer;
}
.btn-cancel:hover { background: #eef2fb; border-color: #b0bedd; }

/* Activator pill */
.pill-dark {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 11px 5px 8px; border-radius: 99px;
  border: none; cursor: pointer; font-size: 11px; font-weight: 600;
  background: rgba(45,75,149,0.12); color: #2D4B95;
  transition: filter 0.15s, transform 0.15s; outline: none;
}
.pill-dark:hover { filter: brightness(1.08); transform: translateY(-1px); }
.pill-dark .v-icon { font-size: 14px !important; color: #2D4B95; }

@media (max-width: 600px) {
  .settings-card { flex-direction: column; align-items: stretch; }
  .btn-action { align-self: flex-end; }
}
</style>

<style>
/* v-dialog aplica max-height: 90% por defecto (teleportado fuera del scope del componente) */
.account-settings-dialog.v-dialog {
  max-height: 92vh;
}
</style>